'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import type { CMSFaq, CMSRichText } from '@/lib/cms/types';
import { getFaqSectionAnchorId } from '../faqAnchors';
import type { FreeTextContentLinks } from '../FreeTextContent';
import { FreeTextContent } from '../FreeTextContent';
import { Heading, type HeadingData } from '../Heading';
import styles from './FaqWrap.module.scss';

// ============================================
// Props Interface - Adapter Output ONLY
// ============================================

export interface FaqWrapProps extends Omit<CMSFaq, '__typename' | 'sys' | 'internalName'> {
  /** Contentful sys.id — used for stable scroll anchors inside FaqCategories */
  sectionSysId?: string;
  /** Whether this is within FaqCategories component */
  isFromFaqWrapCategories?: boolean;
  /** Optional className */
  className?: string;
  /** CMS index for test IDs */
  cmsIndex?: number;
}

// ============================================
// Internal Item Component
// ============================================

interface FaqItemProps {
  question: string;
  answer?: CMSRichText | string | { json?: unknown; links?: CMSRichText['links'] } | null;
}

/**
 * Individual FAQ accordion item
 */
function FaqItem({ question, answer }: FaqItemProps) {
  const t = useTranslations('faq');
  const [isOpen, setIsOpen] = useState(false);

  const renderAnswer = () => {
    if (!answer) {
      return null;
    }

    if (typeof answer === 'string') {
      return <p>{answer}</p>;
    }

    const richText = answer as CMSRichText;
    if (richText.json) {
      return (
        <FreeTextContent
          freeTextContent={{
            json: richText.json,
            links: richText.links as FreeTextContentLinks | undefined,
          }}
        />
      );
    }

    const legacyJson = (answer as { json?: unknown }).json;
    if (legacyJson) {
      return <FreeTextContent freeTextContent={{ json: legacyJson }} />;
    }

    return <p>{t('answerContentAvailable')}</p>;
  };

  return (
    <div className={styles.faqItem}>
      <button
        type="button"
        className={styles.faqQuestion}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        data-testid="FaqItemQuestion"
      >
        <span>{question}</span>
        <svg
          className={clsx(styles.faqChevron, isOpen && styles.open)}
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      <div
        className={clsx(styles.faqAnswer, isOpen && styles.open)}
        data-testid="FaqItemContentWrapper"
      >
        <div className={styles.faqAnswerContent}>{renderAnswer()}</div>
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

/**
 * FAQ Section component
 *
 * Displays a list of FAQ items with accordion-style expand/collapse.
 * Supports collapsing to show only first 3 items with "show more" button.
 *
 * Accepts ONLY adapter output (CMSFaq type).
 *
 * @example
 * <FaqWrap
 *   heading="Frequently Asked Questions"
 *   categories={[{ name: 'General', questions: [...] }]}
 *   collapse={true}
 * />
 */
export function FaqWrap({
  heading,
  categories,
  collapse = true,
  sectionSysId,
  isFromFaqWrapCategories = false,
  className,
  cmsIndex,
}: Readonly<FaqWrapProps>) {
  const t = useTranslations('faq');
  const [showAllItems, setShowAllItems] = useState(!collapse);

  // Flatten all questions from all categories
  const allItems = (categories || []).flatMap((category, categoryIndex) =>
    (category.questions || []).map((q, questionIndex) => ({
      id: q.sys?.id || `faq-${categoryIndex}-${questionIndex}`,
      question: q.question,
      answer: q.answer,
    }))
  );

  // Build heading data from string
  const headingData: HeadingData | null = heading ? { text: heading, headingType: 'h2' } : null;

  if (allItems.length === 0) {
    return null;
  }

  const displayedItems = showAllItems ? allItems : allItems.slice(0, 3);
  const shouldShowMore = !showAllItems && allItems.length > 3;

  const headingId =
    isFromFaqWrapCategories && sectionSysId
      ? getFaqSectionAnchorId(sectionSysId)
      : headingData?.text?.toLowerCase().replace(/\s+/g, '-');

  const testIdPrefix = cmsIndex !== undefined ? `faqwrap-${cmsIndex}` : 'faqwrap';

  return (
    <div
      className={clsx(styles.wrapper, isFromFaqWrapCategories && styles.fromCategories, className)}
      data-testid={cmsIndex !== undefined ? `${testIdPrefix}` : 'FAQSectionWrapper'}
    >
      <div className={clsx(styles.container, isFromFaqWrapCategories && styles.fullWidth)}>
        {headingData &&
          (isFromFaqWrapCategories ? (
            <h2 id={headingId} className={styles.sectionHeading} data-testid="FAQSectionHeading">
              {headingData.text}
            </h2>
          ) : (
            <div id={headingId} className={styles.headingWrapper}>
              <Heading Heading={headingData} data-testid="FAQSectionHeading" />
            </div>
          ))}

        {displayedItems.map((item, index) => (
          <FaqItem key={`${item.id}-${index}`} question={item.question} answer={item.answer} />
        ))}

        {shouldShowMore && (
          <button
            type="button"
            className={styles.moreResults}
            onClick={() => setShowAllItems(true)}
            data-testid="FAQSectionMoreResultsBox"
          >
            <span>{t('moreResults')}</span>
            <svg
              className={styles.moreResultsIcon}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

FaqWrap.displayName = 'FaqWrap';
